import { EWidgetRegion, EWidgetSection } from '#shared';

import { blockActions } from './block.slice';
import { regionActions, SECTION_LIMITS } from './region.slice';
import { sectionActions } from './section.slice';

import type { AppDispatch, RootState } from '../redux-store';
import type { WidgetId } from '../widgetManager';

type SetSectionBlocksPayload = {
  sectionId: EWidgetSection;
  blockIds: WidgetId[];
};

/**
 * Thunk action creator to set section blocks and update block entities with their section assignments.
 * This replaces the extraReducers pattern to avoid cascading state updates.
 *
 * Usage: dispatch(setSectionBlocks({ sectionId, blockIds }))
 */
export const setSectionBlocks =
  ({ sectionId, blockIds }: SetSectionBlocksPayload) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    // First, update the section with new blocks
    dispatch(sectionActions.sectionSetBlocks({ sectionId, blockIds }));

    // Then, update all blocks to reflect their new section assignments
    const state = getState();
    const blocks = Object.values(state.blocks.entities);

    blocks.forEach(block => {
      if (blockIds.includes(block.id)) {
        // Block is in the new section
        dispatch(
          blockActions.blocksUpdate([{ id: block.id, changes: { sectionId } }]),
        );
      } else if (block.sectionId === sectionId) {
        // Block was in this section but is no longer
        dispatch(
          blockActions.blocksUpdate([
            { id: block.id, changes: { sectionId: null } },
          ]),
        );
      }
    });
  };

/**
 * Thunk to clean up an empty section by removing it from both the sections state
 * and the region's sectionOrder. Respects minimum section limits.
 *
 * Usage: dispatch(cleanupEmptySection(sectionId))
 */
export const cleanupEmptySection =
  (sectionId: EWidgetSection) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const section = state.sections.entities[sectionId];

    // Only proceed if section exists and is empty
    if (!section || section.blockOrder.length > 0) {
      return;
    }

    // Find which region contains this section
    const region = state.regions.find(r => r.sectionOrder.includes(sectionId));

    if (!region) {
      // Section not in any region, just remove it
      dispatch(sectionActions.sectionRemoveIfEmpty(sectionId));
      return;
    }

    // Check if we can remove this section (respect minimum limit)
    if (region.sectionOrder.length <= SECTION_LIMITS.MIN) {
      // Can't remove - at minimum sections
      return;
    }

    // Remove from region first
    dispatch(
      regionActions.regionRemoveSection({
        regionId: region.id,
        sectionId,
      }),
    );

    // Then remove the section entity
    dispatch(sectionActions.sectionRemoveIfEmpty(sectionId));
  };

/**
 * Thunk to check and clean up all empty sections in a region.
 * Useful after widget removal.
 *
 * Usage: dispatch(cleanupEmptySectionsInRegion(regionId))
 */
export const cleanupEmptySectionsInRegion =
  (regionId: EWidgetRegion) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const region = state.regions.find(r => r.id === regionId);

    if (!region) {
      return;
    }

    // Find empty sections (but don't remove if it would go below minimum)
    const emptySections = region.sectionOrder.filter(sectionId => {
      const section = state.sections.entities[sectionId];
      return section && section.blockOrder.length === 0;
    });

    // Calculate how many we can remove
    const canRemoveCount = Math.max(
      0,
      region.sectionOrder.length - SECTION_LIMITS.MIN,
    );
    const toRemove = emptySections.slice(0, canRemoveCount);

    // Remove each empty section
    toRemove.forEach(sectionId => {
      dispatch(cleanupEmptySection(sectionId));
    });
  };
